import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, findEGC, motor1phRows, motor3phRows, wireRows, getMotorFLA3ph,
  OCPD_RULES, STD_FUSE, STD_BREAKER, nextStd, NEMA_STARTERS,
  HP_RATED_SWITCHES, NEMA_ENCLOSURES, HP_OPTIONS_3PH, VOLTAGE_OPTIONS_3PH,
  VFD_DATA, REACTOR_RECS, BYPASS_CONTACTORS,
  type FeederMotor,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

export const MotorStarterSizing = ({ onSave }: { onSave?: SaveFn }) => {
  const [hp, setHp] = useState("");
  const [phase, setPhase] = useState<string>(defaults.phases);
  const [voltage, setVoltage] = useState(defaults.phases === "1" ? "230" : defaults.voltage === "480" ? "460" : defaults.voltage);
  const [sf, setSf] = useState("1.15");
  const [ocpdType, setOcpdType] = useState("Dual-Element (Time-Delay) Fuse");

  const voltageOptions = phase === "1" ? ["115", "200", "230"] : ["208", "230", "460", "575"];
  const actualVoltage = voltageOptions.includes(voltage) ? voltage : voltageOptions[0];

  const getFLA = (): number | null => {
    if (!hp) return null;
    if (phase === "1") {
      const row = motor1phRows.find(r => String(r[0]) === hp);
      if (!row) return null;
      const vi = actualVoltage === "115" ? 1 : actualVoltage === "200" ? 2 : 3;
      return row[vi] as number;
    } else {
      const row = motor3phRows.find(r => String(r[0]) === hp);
      if (!row) return null;
      const vi = actualVoltage === "208" ? 1 : actualVoltage === "230" ? 2 : actualVoltage === "460" ? 3 : 4;
      return row[vi] as number;
    }
  };

  const fla = getFLA();
  const sfNum = parseFloat(sf) || 1.15;
  const conductorAmps = fla ? fla * 1.25 : null;
  const olTrip = fla ? fla * (sfNum >= 1.15 ? 1.25 : 1.15) : null;
  const ocpdRule = OCPD_RULES.find(r => r.type === ocpdType);
  const ocpdMultiplier = ocpdRule ? (ocpdRule.fuse > 0 ? ocpdRule.fuse : ocpdRule.breaker) : 1.75;
  const ocpdCalc = fla ? fla * ocpdMultiplier : null;
  const isFuse = ocpdRule ? ocpdRule.fuse > 0 : true;
  const ocpdStd = ocpdCalc ? nextStd(ocpdCalc, isFuse ? STD_FUSE : STD_BREAKER) : null;

  const getNemaSize = () => {
    if (!fla) return null;
    return NEMA_STARTERS.find(s => {
      if (phase === "1") return s.maxFla >= fla;
      if (actualVoltage === "460" || actualVoltage === "575") {
        const key = actualVoltage === "460" ? "maxHp3ph460" : "maxHp3ph575";
        const hpNum = parseFloat(hp) || 0;
        return s[key] >= hpNum;
      }
      const hpNum = parseFloat(hp) || 0;
      return s.maxHp3ph230 >= hpNum;
    }) ?? null;
  };
  const nema = getNemaSize();

  const getWireSize = () => {
    if (!conductorAmps) return null;
    const sizes: { label: string; amps: number }[] = [
      { label: "14 AWG", amps: 15 }, { label: "12 AWG", amps: 20 }, { label: "10 AWG", amps: 30 },
      { label: "8 AWG", amps: 40 }, { label: "6 AWG", amps: 55 }, { label: "4 AWG", amps: 70 },
      { label: "3 AWG", amps: 85 }, { label: "2 AWG", amps: 95 }, { label: "1 AWG", amps: 110 },
      { label: "1/0", amps: 125 }, { label: "2/0", amps: 145 }, { label: "3/0", amps: 165 },
      { label: "4/0", amps: 195 }, { label: "250 kcmil", amps: 215 }, { label: "300 kcmil", amps: 240 },
      { label: "350 kcmil", amps: 260 }, { label: "500 kcmil", amps: 320 },
    ];
    return sizes.find(s => s.amps >= conductorAmps) ?? null;
  };
  const wire = getWireSize();
  const egc = ocpdStd ? findEGC(ocpdStd) : null;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        NEC Article 430 — Enter motor HP, phase & voltage to get OCPD, contactor, overload relay, and wire sizing recommendations.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Motor HP</label>
          <Input type="text" inputMode="text" value={hp} onChange={e => setHp(e.target.value.slice(0, 10))} placeholder="e.g. 10" className="bg-secondary text-foreground border-border font-mono-data text-lg" />
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phase</label>
          <Select value={phase} onValueChange={v => { setPhase(v); setVoltage(v === "1" ? "230" : "460"); }}>
            <SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="1">1Φ</SelectItem><SelectItem value="3">3Φ</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label>
          <Select value={actualVoltage} onValueChange={setVoltage}>
            <SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger>
            <SelectContent>{voltageOptions.map(v => <SelectItem key={v} value={v}>{v}V</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Service Factor</label>
          <Select value={sf} onValueChange={setSf}>
            <SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="1.0">1.0 (Standard)</SelectItem><SelectItem value="1.15">≥ 1.15</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">OCPD Type (NEC 430.52)</label>
        <Select value={ocpdType} onValueChange={setOcpdType}>
          <SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>{OCPD_RULES.map(r => <SelectItem key={r.type} value={r.type}>{r.type}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {fla && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card"><div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">NEC FLA</span><span className="font-mono-data text-xl text-primary font-bold">{fla} A</span></div></div>

          <div className="trade-card">
            <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Branch Circuit OCPD (430.52)</h3>
            <div className="space-y-1">
              <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">{ocpdType}</span><span className="text-muted-foreground">{fmt(ocpdMultiplier * 100, 0)}% × {fmt(fla, 1)}A = {fmt(ocpdCalc!, 1)}A</span></div>
              <div className="flex justify-between items-center"><span className="text-sm font-medium">Max Standard Size</span><span className="font-mono-data text-primary font-bold text-lg">{ocpdStd}A {isFuse ? "Fuse" : "Breaker"}</span></div>
            </div>
          </div>

          <div className="trade-card">
            <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Overload Relay (430.32)</h3>
            <div className="space-y-1">
              <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">SF {sfNum >= 1.15 ? "≥ 1.15" : "< 1.15"} → {sfNum >= 1.15 ? "125" : "115"}% FLA</span><span className="text-muted-foreground">{fmt(fla, 1)} × {sfNum >= 1.15 ? "1.25" : "1.15"}</span></div>
              <div className="flex justify-between items-center"><span className="text-sm font-medium">Max OL Trip Setting</span><span className="font-mono-data text-primary font-bold text-lg">{fmt(olTrip!, 1)} A</span></div>
            </div>
          </div>

          {nema && (
            <div className="trade-card">
              <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">NEMA Contactor / Starter</h3>
              <div className="flex justify-between items-center"><span className="text-sm text-muted-foreground">Minimum NEMA Size</span><span className="font-mono-data text-primary font-bold text-lg">Size {nema.size}</span></div>
              <div className="flex justify-between items-center mt-1"><span className="text-sm text-muted-foreground">Max FLA Rating</span><span className="font-mono-data text-muted-foreground">{nema.maxFla} A</span></div>
            </div>
          )}

          <div className="trade-card">
            <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-2">Conductor Sizing (430.22)</h3>
            <div className="space-y-1">
              <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">125% × {fmt(fla, 1)}A = {fmt(conductorAmps!, 1)}A</span></div>
              {wire && <div className="flex justify-between items-center"><span className="text-sm font-medium">Min Wire Size (Cu, 75°C)</span><span className="font-mono-data text-primary font-bold">{wire.label}</span></div>}
              {egc && <div className="flex justify-between items-center mt-1"><span className="text-sm font-medium">EGC (250.122)</span><span className="font-mono-data text-primary font-bold">{egc.copper}</span></div>}
            </div>
          </div>

          <div className="trade-card bg-primary/5 border-primary/20">
            <h3 className="text-xs text-primary font-semibold uppercase tracking-wider mb-2">📋 Starter Package Summary</h3>
            <div className="text-sm space-y-1">
              <p><span className="text-muted-foreground">Motor:</span> <span className="font-medium">{hp} HP, {phase}Φ, {actualVoltage}V — {fmt(fla, 1)} FLA</span></p>
              <p><span className="text-muted-foreground">OCPD:</span> <span className="font-medium">{ocpdStd}A {isFuse ? "Fuse" : "Breaker"}</span></p>
              <p><span className="text-muted-foreground">Contactor:</span> <span className="font-medium">NEMA Size {nema?.size ?? "—"}</span></p>
              <p><span className="text-muted-foreground">OL Relay:</span> <span className="font-medium">{fmt(olTrip!, 1)}A max trip</span></p>
              <p><span className="text-muted-foreground">Wire:</span> <span className="font-medium">{wire?.label ?? "—"} Cu + {egc?.copper ?? "—"} EGC</span></p>
            </div>
          </div>
          {onSave && (
            <button type="button" onClick={() => {
              onSave({
                calcType: "motor-starter", label: `${hp} HP ${phase}Φ ${actualVoltage}V Motor Starter`,
                inputs: { hp, phase: `${phase}Φ`, voltage: `${actualVoltage}V`, sf, ocpd_type: ocpdType },
                results: [
                  { label: "FLA", value: fmt(fla, 1), unit: "A" },
                  { label: "OCPD", value: String(ocpdStd), unit: `A ${isFuse ? "Fuse" : "Breaker"}` },
                  { label: "OL Trip", value: fmt(olTrip!, 1), unit: "A" },
                  { label: "NEMA Size", value: nema?.size ?? "—", unit: "" },
                  { label: "Wire", value: wire?.label ?? "—", unit: "Cu" },
                  { label: "EGC", value: egc?.copper ?? "—", unit: "" },
                ],
                note: "",
              });
              toast.success("Saved to Job Log");
            }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
          )}
        </div>
      )}

      {hp && !fla && (
        <div className="trade-card border-destructive/30 bg-destructive/5">
          <p className="text-sm text-destructive">No NEC FLA data found for {hp} HP {phase}Φ. Check tables 430.248/430.250 for valid HP ratings.</p>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        ⚠️ Per NEC 430.52 Exception: If the motor won't start on the calculated OCPD size, the next standard size up may be used. Always verify with the motor nameplate and manufacturer data.
      </p>
    </div>
  );
};

export const DisconnectSizing = ({ onSave }: { onSave?: SaveFn }) => {
  const [hp, setHp] = useState("");
  const [phase, setPhase] = useState<string>(defaults.phases);
  const [voltage, setVoltage] = useState(defaults.voltage);
  const [fusible, setFusible] = useState("fusible");

  const voltOpts = phase === "1" ? ["240"] : ["240", "480", "600"];
  const actualV = voltOpts.includes(voltage) ? voltage : voltOpts[0];

  const getFLA = (): number | null => {
    if (!hp) return null;
    if (phase === "1") { const row = motor1phRows.find(r => String(r[0]) === hp); if (!row) return null; return row[3] as number; }
    else { const row = motor3phRows.find(r => String(r[0]) === hp); if (!row) return null; if (actualV === "240") return row[2] as number; if (actualV === "480") return row[3] as number; return row[4] as number; }
  };

  const fla = getFLA();
  const minDiscoAmps = fla ? fla * 1.15 : null;

  const getHpKey = (): keyof typeof HP_RATED_SWITCHES[0] => {
    if (phase === "1") return "hp1ph240";
    if (actualV === "240") return "hp3ph240";
    if (actualV === "480") return "hp3ph480";
    return "hp3ph600";
  };

  const hpNum = parseFloat(hp) || 0;
  const hpKey = getHpKey();
  const matchedSwitch = HP_RATED_SWITCHES.find(s => { const switchHp = s[hpKey] as number; return switchHp >= hpNum && s.amps >= (minDiscoAmps ?? 0); });
  const requiresHeavyDuty = hpNum >= 100;
  const hpOver100 = phase === "3" && ((actualV === "480" && hpNum > 200) || (actualV === "600" && hpNum > 200) || (actualV === "240" && hpNum > 100));

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">NEC 430.110 — Motor disconnect sizing. Selects HP-rated switch based on motor HP, voltage, and phase. Disconnect must be rated ≥ 115% of motor FLA.</p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Motor HP</label><Input type="text" inputMode="text" value={hp} onChange={e => setHp(e.target.value.slice(0, 10))} placeholder="e.g. 25" className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Phase</label><Select value={phase} onValueChange={v => { setPhase(v); }}><SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="1">1Φ</SelectItem><SelectItem value="3">3Φ</SelectItem></SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label><Select value={actualV} onValueChange={setVoltage}><SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger><SelectContent>{voltOpts.map(v => <SelectItem key={v} value={v}>{v}V</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Type</label><Select value={fusible} onValueChange={setFusible}><SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fusible">Fusible</SelectItem><SelectItem value="nonfusible">Non-Fusible</SelectItem></SelectContent></Select></div>
      </div>

      {fla !== null && minDiscoAmps !== null && (
        <div className="space-y-3 animate-fade-in">
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Motor Data</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="trade-card text-center"><div className="text-xs text-muted-foreground">NEC FLA</div><div className="font-mono-data text-xl font-bold text-primary">{fla} A</div></div>
            <div className="trade-card text-center"><div className="text-xs text-muted-foreground">Min Disco (115%)</div><div className="font-mono-data text-xl font-bold text-primary">{fmt(minDiscoAmps, 1)} A</div></div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Recommended Disconnect</h3>
          {hpOver100 ? (
            <div className="trade-card border-destructive/30 bg-destructive/5"><div className="text-sm font-semibold text-destructive">Circuit Breaker / MCS Required</div><p className="text-xs text-muted-foreground mt-1">NEC 430.109(A)(1) — Motors over the HP-rated switch table maximum require a circuit breaker or molded-case switch rated ≥ 115% FLA ({fmt(minDiscoAmps, 0)}A).</p></div>
          ) : matchedSwitch ? (
            <div className="trade-card border-primary/20 bg-primary/5">
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-muted-foreground">Switch Rating:</span><span className="font-mono-data font-bold text-primary text-right">{matchedSwitch.amps}A</span>
                <span className="text-muted-foreground">HP Rating ({actualV}V):</span><span className="font-mono-data font-bold text-primary text-right">{matchedSwitch[hpKey] as number} HP</span>
                <span className="text-muted-foreground">Duty:</span><span className="font-mono-data font-bold text-right">{matchedSwitch.type}</span>
                <span className="text-muted-foreground">Type:</span><span className="font-mono-data font-bold text-right">{fusible === "fusible" ? "Fusible" : "Non-Fusible"}</span>
                <span className="text-muted-foreground">Poles:</span><span className="font-mono-data font-bold text-right">{phase === "1" ? "2P" : "3P"}</span>
              </div>
            </div>
          ) : (
            <div className="trade-card border-destructive/30 bg-destructive/5"><div className="text-sm text-destructive font-semibold">No standard HP-rated switch found</div><p className="text-xs text-muted-foreground mt-1">Use a circuit breaker or molded-case switch rated ≥ {fmt(minDiscoAmps, 0)}A.</p></div>
          )}

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">NEC Requirements</h3>
          <div className="space-y-1.5">
            {[
              { rule: "430.110(A)", text: `Disconnect ≥ 115% FLA (${fmt(minDiscoAmps, 1)}A)` },
              { rule: "430.109(A)", text: "Must be HP-rated switch, CB, or molded-case switch" },
              { rule: "430.110(A)", text: requiresHeavyDuty ? "≥ 100 HP: Heavy-duty type required" : "< 100 HP: General or heavy-duty type" },
              { rule: "430.102(A)", text: "Disconnect must be in sight from controller" },
              { rule: "430.102(B)", text: "Disconnect must be in sight from motor (or lockable)" },
              { rule: "430.113", text: fusible === "fusible" ? "Fusible: Can serve as both disconnect and OCPD" : "Non-fusible: Separate OCPD required" },
            ].map((item, i) => (
              <div key={i} className="flex gap-2 text-xs"><span className="font-mono-data text-primary font-semibold whitespace-nowrap">{item.rule}</span><span className="text-muted-foreground">{item.text}</span></div>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">HP-Rated Switch Table</h3>
      <div className="-mx-4 overflow-x-auto">
        <div className="mx-4 rounded-lg border border-border">
          <table className="w-full text-left">
            <thead><tr><th className="data-table-header">Amps</th><th className="data-table-header">1Φ 240V</th><th className="data-table-header">3Φ 240V</th><th className="data-table-header">3Φ 480V</th><th className="data-table-header">3Φ 600V</th><th className="data-table-header">Duty</th></tr></thead>
            <tbody>
              {HP_RATED_SWITCHES.map((s, i) => {
                const isMatch = matchedSwitch?.amps === s.amps && fla !== null;
                return (
                  <tr key={s.amps} className={isMatch ? "bg-primary/15 ring-1 ring-inset ring-primary/30" : i % 2 === 0 ? "bg-card" : "bg-secondary/30"}>
                    <td className={`data-table-cell font-mono-data ${isMatch ? "text-primary font-bold" : "font-semibold"}`}>{s.amps}A</td>
                    <td className={`data-table-cell font-mono-data ${isMatch ? "text-primary font-bold" : ""}`}>{s.hp1ph240 || "—"}</td>
                    <td className={`data-table-cell font-mono-data ${isMatch ? "text-primary font-bold" : ""}`}>{s.hp3ph240 || "—"}</td>
                    <td className={`data-table-cell font-mono-data ${isMatch ? "text-primary font-bold" : ""}`}>{s.hp3ph480 || "—"}</td>
                    <td className={`data-table-cell font-mono-data ${isMatch ? "text-primary font-bold" : ""}`}>{s.hp3ph600 || "—"}</td>
                    <td className={`data-table-cell text-xs ${isMatch ? "text-primary font-bold" : ""}`}>{s.type}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-2">NEMA Enclosure Types</h3>
      <div className="grid grid-cols-1 gap-1.5">
        {NEMA_ENCLOSURES.map(enc => (
          <div key={enc.type} className="trade-card flex justify-between items-center py-2"><span className="font-mono-data font-bold text-primary">NEMA {enc.type}</span><span className="text-xs text-muted-foreground">{enc.desc}</span></div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ Per NEC 430.110, disconnect must be HP-rated ≥ motor HP and amp-rated ≥ 115% FLA. For Design B energy-efficient motors, verify locked-rotor current compatibility. Switches ≥ 100A must be heavy-duty type per NEC 430.109(C).</p>

      {fla !== null && matchedSwitch && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "disconnect-sizing", label: `${hp} HP ${phase}Φ ${actualV}V — ${matchedSwitch.amps}A ${fusible === "fusible" ? "Fusible" : "Non-Fusible"}`,
            inputs: { hp, phase: `${phase}Φ`, voltage: `${actualV}V`, type: fusible },
            results: [
              { label: "FLA", value: fmt(fla, 1), unit: "A" },
              { label: "Min Disconnect", value: fmt(minDiscoAmps!, 1), unit: "A" },
              { label: "Switch Rating", value: String(matchedSwitch.amps), unit: "A" },
              { label: "Duty", value: matchedSwitch.type, unit: "" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}
    </div>
  );
};

let feederMotorId = 0;

export const MotorFeederSizing = ({ onSave }: { onSave?: SaveFn }) => {
  const defaultMotorV = defaults.voltage === "480" ? "460" : defaults.voltage;
  const [motors, setMotors] = useState<FeederMotor[]>([
    { id: ++feederMotorId, hp: "10", voltage: defaultMotorV },
    { id: ++feederMotorId, hp: "5", voltage: defaultMotorV },
  ]);
  const [ocpdType, setOcpdType] = useState("Dual-Element (Time-Delay) Fuse");

  const addMotor = () => { setMotors(prev => [...prev, { id: ++feederMotorId, hp: "5", voltage: defaultMotorV }]); };
  const removeMotor = (id: number) => { if (motors.length <= 1) return; setMotors(prev => prev.filter(m => m.id !== id)); };
  const updateMotor = (id: number, field: keyof FeederMotor, value: string) => { setMotors(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m)); };

  const motorData = motors.map(m => ({ ...m, fla: getMotorFLA3ph(m.hp, m.voltage) }));
  const allValid = motorData.every(m => m.fla !== null);
  const flas = motorData.map(m => m.fla ?? 0);
  const largestFLA = Math.max(...flas);
  const sumOtherFLAs = flas.reduce((s, f) => s + f, 0) - largestFLA;
  const feederAmps = largestFLA * 1.25 + sumOtherFLAs;

  const ocpdRule = OCPD_RULES.find(r => r.type === ocpdType);
  const ocpdMultiplier = ocpdRule ? (ocpdRule.fuse > 0 ? ocpdRule.fuse : ocpdRule.breaker) : 1.75;
  const largestBranchOCPD = nextStd(largestFLA * ocpdMultiplier, ocpdRule?.fuse ? STD_FUSE : STD_BREAKER);
  const feederOCPD = largestBranchOCPD + sumOtherFLAs;
  const feederOCPDStd = nextStd(feederOCPD, ocpdRule?.fuse ? STD_FUSE : STD_BREAKER);

  const findWire = (amps: number) => { const row = wireRows.find(r => (r[3] as number) >= amps); return row ? `${row[0]} AWG` : "Exceeds table"; };
  const egc = findEGC(feederOCPDStd);

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">NEC 430.24 & 430.62 — Size feeder conductors and OCPD for multiple motors on a common feeder.</p>

      <div className="space-y-2">
        <div className="flex items-center justify-between"><h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Motors on Feeder</h3><button onClick={addMotor} className="text-xs font-semibold text-primary hover:text-primary/80 transition-colors">+ Add Motor</button></div>

        {motors.map((motor, i) => {
          const fla = motorData[i]?.fla;
          const isLargest = fla !== null && fla === largestFLA && flas.filter(f => f === largestFLA).length === 1 ? true : i === motorData.findIndex(m => m.fla === largestFLA);
          return (
            <div key={motor.id} className={`trade-card flex items-center gap-2 ${isLargest ? "border-primary/30 bg-primary/5" : ""}`}>
              <div className="flex-1 grid grid-cols-3 gap-2 items-center">
                <div className="text-xs text-muted-foreground font-semibold">M{i + 1}{isLargest && <span className="ml-1 text-primary text-[10px]">★</span>}</div>
                <Select value={motor.hp} onValueChange={v => updateMotor(motor.id, "hp", v)}><SelectTrigger className="bg-secondary border-border font-mono-data text-sm h-8"><SelectValue /></SelectTrigger><SelectContent>{HP_OPTIONS_3PH.map(hp => <SelectItem key={hp} value={hp}>{hp} HP</SelectItem>)}</SelectContent></Select>
                <Select value={motor.voltage} onValueChange={v => updateMotor(motor.id, "voltage", v)}><SelectTrigger className="bg-secondary border-border font-mono-data text-sm h-8"><SelectValue /></SelectTrigger><SelectContent>{VOLTAGE_OPTIONS_3PH.map(v => <SelectItem key={v} value={v}>{v}V</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="text-right min-w-[50px]"><span className="font-mono-data text-sm font-bold text-primary">{fla ?? "—"}A</span></div>
              {motors.length > 1 && <button onClick={() => removeMotor(motor.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1">✕</button>}
            </div>
          );
        })}
      </div>

      <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Feeder OCPD Type</label><Select value={ocpdType} onValueChange={setOcpdType}><SelectTrigger className="bg-secondary border-border font-mono-data text-sm"><SelectValue /></SelectTrigger><SelectContent>{OCPD_RULES.filter(r => r.type !== "Instantaneous-Trip Breaker").map(r => <SelectItem key={r.type} value={r.type}>{r.type}</SelectItem>)}</SelectContent></Select></div>

      {allValid && (
        <div className="space-y-3 animate-fade-in">
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Feeder Calculation Summary</h3>
          <div className="trade-card space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-muted-foreground">Largest motor FLA (M{motorData.findIndex(m => m.fla === largestFLA) + 1})</span><span className="font-mono-data font-bold">{largestFLA}A</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">125% × largest FLA</span><span className="font-mono-data font-bold">{fmt(largestFLA * 1.25, 1)}A</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Sum of other motor FLAs</span><span className="font-mono-data font-bold">{fmt(sumOtherFLAs, 1)}A</span></div>
            <div className="border-t border-border pt-2 flex justify-between"><span className="text-muted-foreground font-semibold">Min. feeder conductor ampacity (430.24)</span><span className="font-mono-data font-bold text-primary">{fmt(feederAmps, 1)}A</span></div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Feeder Conductor</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{findWire(feederAmps)}</div><div className="text-[10px] text-muted-foreground">≥ {fmt(feederAmps, 1)}A capacity</div></div>
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Feeder OCPD (430.62)</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{feederOCPDStd}A</div><div className="text-[10px] text-muted-foreground">{ocpdType.split(" ")[0]} {ocpdRule?.fuse ? "Fuse" : "Breaker"}</div></div>
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Total FLA</div><div className="font-mono-data text-lg font-bold text-foreground mt-1">{fmt(flas.reduce((s, f) => s + f, 0), 1)}A</div><div className="text-[10px] text-muted-foreground">{motors.length} motor{motors.length !== 1 ? "s" : ""}</div></div>
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">EGC (250.122)</div><div className="font-mono-data text-lg font-bold text-foreground mt-1">{egc?.copper ?? "—"}</div><div className="text-[10px] text-muted-foreground">Based on {feederOCPDStd}A OCPD</div></div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">NEC References</h3>
          <div className="space-y-1.5">
            {[
              { rule: "430.24", text: `Feeder = 125% largest (${fmt(largestFLA * 1.25, 1)}A) + others (${fmt(sumOtherFLAs, 1)}A) = ${fmt(feederAmps, 1)}A` },
              { rule: "430.62", text: `OCPD = largest branch OCPD (${largestBranchOCPD}A) + sum others (${fmt(sumOtherFLAs, 0)}A) → ${feederOCPDStd}A` },
              { rule: "430.24(Ex)", text: "For identical motors starting simultaneously, apply 125% to all" },
              { rule: "430.25", text: "Multi-motor & combination-load equipment same rules" },
            ].map((item, i) => (<div key={i} className="flex gap-2 text-xs"><span className="font-mono-data text-primary font-semibold whitespace-nowrap">{item.rule}</span><span className="text-muted-foreground">{item.text}</span></div>))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ All motors assumed 3Φ. FLA from NEC Table 430.250. For mixed 1Φ/3Φ feeders, calculate each motor's FLA separately.</p>

      {allValid && onSave && (
        <button type="button" onClick={() => {
          const motorDesc = motors.map((m, i) => `M${i+1}: ${m.hp}HP@${m.voltage}V`).join(", ");
          onSave({
            calcType: "motor-feeder", label: `${motors.length}-Motor Feeder — ${fmt(feederAmps, 1)}A`,
            inputs: { motors: motorDesc, ocpd_type: ocpdType },
            results: [
              { label: "Feeder Amps", value: fmt(feederAmps, 1), unit: "A" },
              { label: "Feeder OCPD", value: String(feederOCPDStd), unit: "A" },
              { label: "Feeder Wire", value: findWire(feederAmps), unit: "Cu" },
              { label: "EGC", value: egc?.copper ?? "—", unit: "" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}
    </div>
  );
};

export const VFDSizingTool = ({ onSave }: { onSave?: SaveFn }) => {
  const [hp, setHp] = useState("10");
  const [voltage, setVoltage] = useState(defaults.voltage === "480" ? "460" : defaults.voltage);
  const [cableLength, setCableLength] = useState("50");
  const [hasBypass, setHasBypass] = useState(true);
  const [availableSC, setAvailableSC] = useState("65");

  const vfd = VFD_DATA.find(v => v.hp === hp);
  const is460 = voltage === "460";
  const inputAmps = vfd ? (is460 ? vfd.inputA460 : vfd.inputA230) : null;
  const outputAmps = vfd ? (is460 ? vfd.outputA460 : vfd.outputA230) : null;
  const kva = vfd ? (is460 ? vfd.kva460 : vfd.kva230) : null;
  const cableFt = parseFloat(cableLength) || 0;
  const scKA = parseFloat(availableSC) || 0;

  const motorFLA = vfd ? getMotorFLA3ph(hp, voltage === "460" ? "460" : "230") : null;
  const inputWire = inputAmps ? wireRows.find(r => (r[3] as number) >= inputAmps) : null;
  const outputWireAmps = motorFLA ? motorFLA * 1.25 : null;
  const outputWire = outputWireAmps ? wireRows.find(r => (r[3] as number) >= outputWireAmps) : null;

  const hpNum = parseFloat(hp) || 0;
  const bypassContactor = is460 ? BYPASS_CONTACTORS.find(c => c.maxHp460 >= hpNum) : BYPASS_CONTACTORS.find(c => c.maxHp230 >= hpNum);
  const vfdOCPD = inputAmps ? nextStd(inputAmps * 1.5, STD_FUSE) : null;

  const needsInputReactor = scKA > 100 || (kva && scKA > 0);
  const needsOutputReactor = cableFt > 100;

  return (
    <div className="space-y-4">
      <p className="text-[11px] text-muted-foreground leading-relaxed">VFD sizing tool — Select motor HP and voltage to get drive sizing, conductor requirements, bypass contactor, and reactor recommendations.</p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Motor HP</label><Select value={hp} onValueChange={setHp}><SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger><SelectContent>{VFD_DATA.map(v => <SelectItem key={v.hp} value={v.hp}>{v.hp} HP</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Voltage</label><Select value={voltage} onValueChange={setVoltage}><SelectTrigger className="bg-secondary border-border font-mono-data text-lg"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="230">230V</SelectItem><SelectItem value="460">460V</SelectItem></SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Cable to Motor (ft)</label><Input type="number" inputMode="numeric" value={cableLength} onChange={e => setCableLength(e.target.value.slice(0, 5))} placeholder="50" className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Available SC (kA)</label><Input type="number" inputMode="numeric" value={availableSC} onChange={e => setAvailableSC(e.target.value.slice(0, 5))} placeholder="65" className="bg-secondary text-foreground border-border font-mono-data text-lg" /></div>
      </div>

      <div className="flex items-center justify-between trade-card py-2">
        <span className="text-sm text-foreground font-medium">Include Bypass Contactor</span>
        <button onClick={() => setHasBypass(!hasBypass)} className={`w-11 h-6 rounded-full transition-colors relative ${hasBypass ? "bg-primary" : "bg-secondary"}`}>
          <div className={`w-5 h-5 rounded-full bg-primary-foreground shadow-sm absolute top-0.5 transition-transform ${hasBypass ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      {vfd && inputAmps && outputAmps && (
        <div className="space-y-3 animate-fade-in">
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">VFD Specifications</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Drive Rating</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{kva} kVA</div><div className="text-[10px] text-muted-foreground">{hp} HP</div></div>
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Input Amps</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{inputAmps}A</div><div className="text-[10px] text-muted-foreground">Line side</div></div>
            <div className="trade-card text-center border-primary/20 bg-primary/5"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Output Amps</div><div className="font-mono-data text-lg font-bold text-primary mt-1">{outputAmps}A</div><div className="text-[10px] text-muted-foreground">Motor side</div></div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Conductor Sizing</h3>
          <div className="grid grid-cols-2 gap-2">
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Input Conductors</div><div className="font-mono-data text-lg font-bold text-foreground mt-1">{inputWire ? `${inputWire[0]} AWG` : "—"}</div><div className="text-[10px] text-muted-foreground">≥ {fmt(inputAmps, 1)}A (NEC 430.122)</div></div>
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Output Conductors</div><div className="font-mono-data text-lg font-bold text-foreground mt-1">{outputWire ? `${outputWire[0]} AWG` : "—"}</div><div className="text-[10px] text-muted-foreground">≥ {fmt(outputWireAmps ?? 0, 1)}A (125% FLA)</div></div>
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Input OCPD</div><div className="font-mono-data text-lg font-bold text-foreground mt-1">{vfdOCPD ?? "—"}A</div><div className="text-[10px] text-muted-foreground">Max per VFD nameplate</div></div>
            <div className="trade-card text-center"><div className="text-[10px] text-muted-foreground uppercase tracking-wider">Motor FLA (NEC)</div><div className="font-mono-data text-lg font-bold text-foreground mt-1">{motorFLA ?? "—"}A</div><div className="text-[10px] text-muted-foreground">Table 430.250</div></div>
          </div>

          {hasBypass && (
            <>
              <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Bypass Contactor</h3>
              {bypassContactor ? (
                <div className="trade-card border-copper/30 bg-copper/5">
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <span className="text-muted-foreground">NEMA Size:</span><span className="font-mono-data font-bold text-right">Size {bypassContactor.size}</span>
                    <span className="text-muted-foreground">Max HP ({is460 ? "460V" : "230V"}):</span><span className="font-mono-data font-bold text-right">{is460 ? bypassContactor.maxHp460 : bypassContactor.maxHp230} HP</span>
                    <span className="text-muted-foreground">Max Amps:</span><span className="font-mono-data font-bold text-right">{bypassContactor.maxA}A</span>
                  </div>
                </div>
              ) : (
                <div className="trade-card border-destructive/30 bg-destructive/5"><span className="text-sm text-destructive">Motor exceeds standard contactor table</span></div>
              )}
              <p className="text-[11px] text-muted-foreground leading-relaxed">⚠️ Bypass must include mechanical interlock. Motor OL relay required in bypass mode.</p>
            </>
          )}

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Reactor Recommendations</h3>
          <div className="space-y-2">
            <div className={`trade-card ${needsInputReactor ? "border-safety/30 bg-safety/5" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Input Line Reactor</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${scKA > 100 ? "bg-destructive/15 text-destructive" : needsInputReactor ? "bg-safety/15 text-safety" : "bg-secondary text-muted-foreground"}`}>{scKA > 100 ? "Required" : needsInputReactor ? "Recommended" : "Optional"}</span>
              </div>
              <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                <div>• Impedance: <span className="font-mono-data font-semibold text-foreground">{scKA > 100 ? "5%" : "3%"}</span> minimum</div>
                <div>• Rating: <span className="font-mono-data font-semibold text-foreground">{inputAmps}A, {is460 ? "480" : "240"}V</span></div>
              </div>
            </div>
            <div className={`trade-card ${needsOutputReactor ? "border-safety/30 bg-safety/5" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">Output Reactor / dV/dt Filter</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cableFt > 200 ? "bg-destructive/15 text-destructive" : needsOutputReactor ? "bg-safety/15 text-safety" : "bg-secondary text-muted-foreground"}`}>{cableFt > 200 ? "Required" : needsOutputReactor ? "Recommended" : "Optional"}</span>
              </div>
              <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                <div>• Cable length: <span className="font-mono-data font-semibold text-foreground">{cableFt} ft</span> {cableFt > 100 ? "(exceeds 100 ft limit)" : ""}</div>
              </div>
            </div>
          </div>

          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Reactor Selection Guide</h3>
          <div className="-mx-4 overflow-x-auto">
            <div className="mx-4 rounded-lg border border-border">
              <table className="w-full text-left">
                <thead><tr><th className="data-table-header">Condition</th><th className="data-table-header">Impedance</th><th className="data-table-header">Note</th></tr></thead>
                <tbody>{REACTOR_RECS.map((r, i) => (<tr key={i} className={i % 2 === 0 ? "bg-card" : "bg-secondary/30"}><td className="data-table-cell text-xs">{r.condition}</td><td className="data-table-cell font-mono-data font-semibold">{r.pct}</td><td className="data-table-cell text-xs text-muted-foreground">{r.note}</td></tr>))}</tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {vfd && inputAmps && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "vfd-sizing", label: `${hp} HP VFD @ ${voltage}V — ${kva} kVA`,
            inputs: { hp: `${hp} HP`, voltage: `${voltage}V`, cable_length: `${cableLength}ft`, bypass: hasBypass ? "Yes" : "No" },
            results: [
              { label: "Drive Rating", value: String(kva), unit: "kVA" },
              { label: "Input Amps", value: String(inputAmps), unit: "A" },
              { label: "Output Amps", value: String(outputAmps), unit: "A" },
              { label: "Input Wire", value: inputWire ? `${inputWire[0]} AWG` : "—", unit: "" },
              { label: "Input OCPD", value: String(vfdOCPD), unit: "A" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">⚠️ VFD input amps are approximate. Always verify with actual VFD nameplate data. Use inverter-duty motors for VFD applications.</p>
    </div>
  );
};
